import { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  HeadContent,
  Link,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useRouter,
} from "@tanstack/react-router";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { reportVibeError } from "@/lib/vibe-error-reporting";
import appCss from "../styles.css?url";
import { canonicalUrl, SITE_ORIGIN } from "@/lib/site";
import { serviceAreaCities } from "@/data/serviceAreas";
import { useHydrated } from "@/hooks/useHydrated";
import { ChatWidget } from "@/components/chat/ChatWidget";
const siteTitle = "Scotty's Land Specialist | Property Services Highland UT";
const siteDescription =
  "Dumpster rental, land clearing, weed control, spring and fall cleanup, and snow removal in Highland, Utah. Call Scotty's for a quote.";
const siteImage =
  "https://vibe.filesafe.space/1792678768838541728/attachments/4807b41e-2583-45bd-b584-b9a7c8c48a20.jpg";
const structuredData = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "Scotty's Land Specialist LLC",
  image: siteImage,
  telephone: "+18014003346",
  email: "scottysdetail@gmail.com",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Highland",
    addressRegion: "UT",
    addressCountry: "US",
  },
  url: canonicalUrl("/"),
  areaServed: serviceAreaCities.map((city) => ({
    "@type": "City",
    name: `${city.name}, Utah`,
  })),
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "11:00",
      closes: "19:00",
    },
  ],
  priceRange: "$$",
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const hydrated = useHydrated();

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <Outlet />
        {hydrated && <ChatWidget />}
      </TooltipProvider>
    </QueryClientProvider>
  );
}

function RootErrorComponent({
  error,
  reset,
}: {
  error: Error;
  reset: () => void;
}) {
  const router = useRouter();

  useEffect(() => {
    console.error(error);
    reportVibeError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
      <section className="max-w-md space-y-5 text-center">
        <p className="font-display text-sm font-bold uppercase tracking-[0.2em] text-accent">
          Something went wrong
        </p>
        <h1 className="font-display text-5xl font-bold uppercase">
          This page didn't load
        </h1>
        <p className="text-muted-foreground">
          Please try again, or head home and continue exploring.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <button
            type="button"
            className="rounded-full bg-primary px-5 py-3 font-semibold text-primary-foreground transition-colors hover:bg-primary-dark"
            onClick={() => {
              router.invalidate();
              reset();
            }}
          >
            Try again
          </button>
          <Link
            className="rounded-full border border-border px-5 py-3 font-semibold transition-colors hover:bg-muted"
            to="/"
          >
            Go home
          </Link>
        </div>
      </section>
    </main>
  );
}

function RootNotFoundComponent() {
  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
      <section className="max-w-md space-y-5 text-center">
        <p className="font-display text-sm font-bold uppercase tracking-[0.2em] text-accent">
          404
        </p>
        <h1 className="font-display text-5xl font-bold uppercase">
          Page not found
        </h1>
        <p className="text-muted-foreground">
          The page you requested could not be found.
        </p>
        <Link
          className="inline-flex rounded-full bg-primary px-5 py-3 font-semibold text-primary-foreground transition-colors hover:bg-primary-dark"
          to="/"
        >
          Go home
        </Link>
      </section>
    </main>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()(
  {
    head: () => ({
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        { title: siteTitle },
        { name: "description", content: siteDescription },
        { name: "author", content: "Scotty's Land Specialist LLC" },
        { property: "og:title", content: siteTitle },
        { property: "og:description", content: siteDescription },
        { property: "og:type", content: "website" },
        { property: "og:image", content: siteImage },
        { property: "og:url", content: canonicalUrl("/") },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: siteTitle },
        { name: "twitter:description", content: siteDescription },
        { name: "twitter:image", content: siteImage },
      ],
      links: [
        { rel: "stylesheet", href: appCss },
        { rel: "preconnect", href: "https://fonts.googleapis.com" },
        {
          rel: "preconnect",
          href: "https://fonts.gstatic.com",
          crossOrigin: "anonymous",
        },
        {
          rel: "stylesheet",
          href: "https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@500;600;700;800;900&family=Inter:wght@300;400;500;600;700&display=swap",
        },
        { rel: "icon", type: "image/png", href: "/favicon.png" },
        { rel: "apple-touch-icon", href: "/favicon.png" },
        {
          rel: "ai-catalog",
          href: `${SITE_ORIGIN}/.well-known/ai-catalog.json`,
        },
        { rel: "ard", href: `${SITE_ORIGIN}/.well-known/ard.json` },
      ],
      scripts: [
        { src: "/trusted-types-policy.js" },
        { type: "application/ld+json", children: structuredData },
      ],
    }),
    shellComponent: RootShell,
    component: RootComponent,
    notFoundComponent: RootNotFoundComponent,
    errorComponent: RootErrorComponent,
  },
);
